/* eslint-disable */
/**
 * Series, ejes, barras y tooltips del gráfico de evolución.
 *
 * Los métodos operan sobre la instancia de PanelLogic y no mantienen estado global.
 */
import { spacedAxisTicks } from '../utils/chartAxisTicks';

const logic = {
  evoSimple(st, tol) {
    const D = this.data,
      N = D.days.length,
      nC = D.rows.length;
    const EM = {
      alarms: {
        label: 'Alarmas',
        sum: true,
        get: c => c.al,
        fmt: v => String(Math.round(v)),
        bad: 'up'
      },
      apv: {
        label: 'Alarmas por visita',
        sum: true,
        get: c => c.apv,
        fmt: v => v.toFixed(2),
        bad: 'up'
      },
      apt: {
        label: 'Alarmas / t',
        sum: true,
        get: c => c.apt,
        fmt: v => v.toFixed(2),
        bad: 'up'
      },
      ppa: {
        label: 'Pellets perdidos',
        sum: true,
        get: c => c.ppa,
        fmt: v => String(Math.round(v)),
        bad: 'up'
      },
      visitas: {
        label: 'Visitas',
        sum: true,
        get: c => c.vis,
        fmt: v => String(Math.round(v))
      },
      kg: {
        label: 'Alimento entregado',
        sum: true,
        get: c => c.kg,
        // The backend value stored as kg is already expressed in tonnes.
        fmt: v => v.toLocaleString('es-CL', { maximumFractionDigits: 3 }) + ' ton',
        tickFmt: (v, step) => v.toLocaleString('es-CL', { maximumFractionDigits: Math.min(20, Math.max(0, 1 - Math.floor(Math.log10(step)))) }),
        color: '#41a3cf',
        unit: 'ton',
        pattern: c => 'repeating-linear-gradient(135deg,transparent,transparent 0.277778vh,rgba(4,10,20,0.85) 0.277778vh,rgba(4,10,20,0.85) 0.462963vh),' + c
      },
      sfr: {
        label: 'SFR',
        get: c => c.sfr,
        fmt: v => v.toFixed(0) + ' %',
        bad: 'far'
      }
    };
    // agg returns null for periods without data; format those as a dash instead of throwing.
    Object.values(EM).forEach(m => {
      const f = m.fmt;
      m.fmt = v => v == null || !Number.isFinite(v) ? '—' : f(v);
    });
    const k = EM[st.sMetric] ? st.sMetric : 'alarms',
      em = EM[k],
      isFood = k === 'kg',
      week = this.props.groupMode === 'week',
      inv = st.sAxis === 'jaula';
    const pal = ['oklch(0.78 0.12 250)', 'oklch(0.78 0.14 160)', 'oklch(0.72 0.16 320)', 'oklch(0.84 0.14 90)', 'oklch(0.72 0.16 320)', 'oklch(0.78 0.11 200)', 'oklch(0.74 0.15 55)', 'oklch(0.7 0.12 290)'];
    const agg = cs => this.v1MetricAggregate(k, cs);
    let buckets = this.periodBuckets(week);
    const zr = !inv || st.sMix ? st.sZoom : null;
    if (zr) {
      const fb = buckets.filter(b => b[b.length - 1] >= zr[0] && b[0] <= zr[1]);
      if (fb.length) buckets = fb;
    }
    this._evoB = buckets;
    const nB = buckets.length;
    const g = D.rows.map(r => buckets.map(b => agg(b.map(d => r.cells[d]))));
    const last = D.rows.map(r => agg(inv ? r.cells : r.cells.slice(-7))),
      prev = D.rows.map(r => inv ? null : agg(r.cells.slice(-14, -7)));
    const score = v => v == null ? -1 : em.bad === 'far' ? Math.abs(v - 100) : v;
    const order = [...Array(nC).keys()].sort((a, b) => score(last[b]) - score(last[a]));
    const top3 = order.slice(0, 3);
    const sel = [st.sel, ...(st.cmp8 || []).filter(c => c !== st.sel)];
    const setSel = arr => this.setState({
      cmp8: arr.filter(c => c !== st.sel)
    });
    const colorOf = ci => pal[sel.indexOf(ci) % pal.length];
    const shown = sel.map(ci => g[ci] || []).flat().filter(Number.isFinite);
    const rawMax = k === 'sfr' ? Math.max(150, ...shown) : Math.max(...shown, 0.001) * 1.05;
    const step = (() => {
      const s = rawMax / 5,
        p = Math.pow(10, Math.floor(Math.log10(s))),
        n = s / p;
      return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
    })();
    const max = step * Math.ceil(rawMax / step);
    const bars = true;
    const X = i => bars ? i + 0.5 : nB > 1 ? i : 0.5,
      Y = v => (100 - v / max * 100).toFixed(2);
    const path = row => {
      let d = '',
        pen = false;
      row.forEach((v, i) => {
        if (v == null) {
          pen = false;
          return;
        }
        d += (pen ? 'L' : 'M') + X(i) + ' ' + Y(v) + ' ';
        pen = true;
      });
      return d || 'M0 0';
    };
    const lastVal = row => {
      for (let i = row.length - 1; i >= 0; i--) if (row[i] != null) return row[i];
      return 0;
    };
    const ends = sel.map(ci => ({
      lab: D.rows[ci].name,
      c: colorOf(ci),
      t: +Y(lastVal(g[ci]))
    })).sort((a, b) => a.t - b.t);
    let pv = -99;
    ends.forEach(e => {
      if (e.t - pv < 6) e.t = pv + 6;
      pv = e.t;
      e.top = e.t + '%';
    });
    const hDay = st.hover ? st.hover.di : null,
      hb = hDay == null ? -1 : buckets.findIndex(b => b.includes(hDay));
    const idx = e => {
      const r = e.currentTarget.getBoundingClientRect(),
        fx = (e.clientX - r.left) / r.width;
      return Math.max(0, Math.min(nB - 1, bars ? Math.floor(fx * nB) : Math.round(fx * (nB - 1))));
    };
    const xPct = i => bars ? (i + 0.5) / nB * 100 : nB > 1 ? i / Math.max(1, nB - 1) * 100 : 50;
    const nS = Math.max(1, sel.length),
      slot = 0.8 / nS;
    const foodMarks = [],
      foodMark = (value, x0, x1, yMax, n, c) => ({
        l: (x0 / n * 100).toFixed(3) + '%',
        w: ((x1 - x0) / n * 100).toFixed(3) + '%',
        h: (value / yMax * 100).toFixed(2) + '%',
        bg: em.pattern(c),
        c
      }),
      foodAxis = ticks => [{ name: em.label + ' (ton)', short: 'Alimento (ton)', c: em.color, ticks }];
    const sBars = !bars ? [] : sel.map((ci, j) => {
      let d = '';
      g[ci].forEach((v, i) => {
        if (v == null || v <= 0) return;
        const x0 = i + 0.1 + j * slot,
          x1 = x0 + slot * (nS > 1 ? 0.88 : 1);
        if (isFood) {
          foodMarks.push(foodMark(v, x0, x1, max, nB, colorOf(ci)));
          return;
        }
        d += 'M' + x0.toFixed(3) + ' 100 V' + Y(v) + ' H' + x1.toFixed(3) + ' V100 Z ';
      });
      return {
        d: d || 'M0 0',
        c: colorOf(ci)
      };
    });
    // Zero is a valid backend value, but V2 keeps the chart clean by not
    // rendering it as a data label. The axis origin remains visible.
    const fmtV = v => v == null || v === 0 ? '—' : em.fmt(v);
    const pctCh = (a, b) => a == null || b == null || !b ? null : (a - b) / b * 100;
    const good = 'oklch(0.78 0.13 160)',
      badC = 'oklch(0.72 0.17 25)';
    const cLast = agg(D.rows.flatMap(r => inv ? r.cells : r.cells.slice(-7))),
      cPrev = inv ? null : agg(D.rows.flatMap(r => r.cells.slice(-14, -7))),
      insightPeriod = inv ? N > 1 ? D.days[0] + ' – ' + D.days[N - 1] : D.days[0] : 'Últimos 7 días';
    let insight;
    const w = order[0],
      wName = D.rows[w].name;
    if (k === 'sfr' && !cLast) {
      insight = insightPeriod + ': sin registros de SFR.';
    } else if (k === 'sfr') {
      const low = order.filter(ci => last[ci] != null && last[ci] < 100 - tol).map(ci => D.rows[ci].name),
        high = order.filter(ci => last[ci] != null && last[ci] > 100 + tol).map(ci => D.rows[ci].name);
      insight = insightPeriod + ': ' + (low.length ? low.length + ' jaulas bajo el objetivo (' + low.slice(0, 4).join(', ') + (low.length > 4 ? '…' : '') + ')' : 'ninguna jaula bajo el objetivo') + (high.length ? ' y ' + high.length + ' sobre el objetivo.' : '.') + ' La más alejada es ' + wName + ' (' + em.fmt(last[w]) + ').';
    } else if (!cLast) {
      insight = insightPeriod + ': sin registros de ' + em.label.toLowerCase() + '.';
    } else {
      const ch = pctCh(cLast, cPrev),
        chTxt = ch == null ? '' : ' (' + (ch >= 0 ? '↑ ' : '↓ ') + Math.abs(ch).toFixed(0) + ' % vs semana anterior)';
      const tot = em.sum ? 'el total fue ' + em.fmt(cLast) + ' ' + em.label.toLowerCase() : 'el valor fue ' + em.fmt(cLast);
      const share = em.sum && cLast ? ', con el ' + Math.round(last[w] / cLast * 100) + ' % del total' : ' (' + em.fmt(last[w]) + ')';
      insight = insightPeriod + ': ' + tot + chTxt + '. La jaula con más ' + em.label.toLowerCase() + ' es ' + wName + share + '.';
    }
    const xt = spacedAxisTicks(buckets.map((b, i) => ({
      pos: xPct(i) + '%',
      lab: this.periodLabel(b)
    })), st.sAxisWidth, st.sAxisFont);
    const tipRows = hb < 0 ? [] : sel.map(ci => ({
      name: D.rows[ci].name,
      c: colorOf(ci),
      v: g[ci][hb]
    })).filter(r => Number.isFinite(r.v) && r.v !== 0).sort((a, b) => b.v - a.v).map(r => ({
      ...r,
      val: fmtV(r.v)
    }));
    const hbb = hb < 0 ? null : buckets[hb];
    const out = {
      sLeave: () => this.setState({
        hover: null,
        sHc: null
      }),
      sTipFoot: this.props.groupMode === 'day' ? 'Clic para ver este día en el detalle' : 'Clic para ver el inicio de este período en el detalle',
      showTypes: true,
      sMetrics: Object.keys(EM).map(key => ({
        label: EM[key].label,
        bg: key === k ? 'rgba(46,179,238,0.16)' : 'transparent',
        color: key === k ? '#e4e9f0' : '#8793a6',
        fw: key === k ? 600 : 400,
        onClick: () => this.setState({
          sMetric: key
        })
      })),
      sGroups: [],
      sInsight: insight,
      sVB: bars ? '0 0 ' + nB + ' 100' : '0 0 ' + Math.max(1, nB - 1) + ' 100',
      sBars: isFood ? [] : sBars,
      sAvgC: bars ? '#e4e9f0' : '#8793a6',
      sAvgW: bars ? 2 : 3,
      sLegend: bars ? sel.map(ci => ({
        name: D.rows[ci].name,
        c: colorOf(ci)
      })) : [],
      sLegend8: sel.map(ci => ({
        name: D.rows[ci].name,
        c: colorOf(ci)
      })),
      colorOf8: colorOf,
      sTypes: [['barras', 'Barras'], ['lineas', 'Líneas']].map(([key, label]) => ({
        label,
        bg: (bars ? 'barras' : 'lineas') === key ? 'rgba(46,179,238,0.16)' : 'transparent',
        color: (bars ? 'barras' : 'lineas') === key ? '#e4e9f0' : '#8793a6',
        onClick: () => this.setState({
          sType: key
        })
      })),
      sYTicks: [...Array(Math.round(max / step) + 1)].map((_, i) => ({
        pos: (i * step / max * 100).toFixed(2) + '%',
        lab: em.tickFmt ? em.tickFmt(i * step, step) : step < 1 ? (i * step).toFixed(step < 0.1 ? 2 : 1) : String(Math.round(i * step))
      })),
      sXTicks: xt,
      sAvg: 'M0 0',
      sLines: bars ? [] : sel.map(ci => ({
        d: path(g[ci]),
        c: colorOf(ci)
      })),
      sEnd: (() => {
        const L = bars ? [] : ends.map(e => ({
          lab: e.lab,
          c: e.c,
          t: parseFloat(e.top)
        }));
        if (k === 'sfr') L.push({
          lab: '100 % obj.',
          c: '#E8500F',
          t: +Y(100)
        });
        L.sort((a, b) => a.t - b.t);
        let p = -99;
        L.forEach(e => {
          if (e.t - p < 6) e.t = p + 6;
          p = e.t;
          e.top = e.t + '%';
        });
        return L;
      })(),
      sRef: k === 'sfr' ? 'M0 ' + Y(100) + ' H' + (bars ? nB : nB - 1) : 'M0 0',
      sRefDisp: k === 'sfr' ? 'block' : 'none',
      sRefPos: (100 / max * 100).toFixed(2) + '%',
      sMove: e => {
        const di = buckets[idx(e)][0];
        const x = this.state.hover;
        if (!x || x.di !== di) this.setState({
          hover: {
            ci: st.sel,
            di
          }
        });
      },
      sClick: e => this.setState({
        day: buckets[idx(e)][0]
      }),
      sX: hb < 0 ? '0%' : xPct(hb) + '%',
      sTipDisp: hb < 0 ? 'none' : 'flex',
      sTipTx: hb >= 0 && xPct(hb) > 60 ? "translateX(calc(-100% - 0.625vw))" : "translateX(0.625vw)",
      sTipDate: hbb ? this.periodLabel(hbb, true) : '',
      sTipRows: tipRows,
      sValHead: em.sum ? 'Total' : 'Prom.',
      sTop3: () => setSel(top3),
      sNone: () => setSel([]),
      sList: order.map(ci => {
        const on = sel.includes(ci),
          ch = k === 'sfr' ? last[ci] != null && prev[ci] != null ? last[ci] - prev[ci] : null : pctCh(last[ci], prev[ci]);
        let tc = '#5a667a',
          trend = '—';
        if (ch != null) {
          trend = (ch >= 0 ? '↑ ' : '↓ ') + Math.abs(ch).toFixed(0) + (k === 'sfr' ? ' pts' : ' %');
          if (em.bad === 'up') tc = Math.abs(ch) < 5 ? '#8793a6' : ch > 0 ? badC : good;else if (em.bad === 'far') tc = Math.abs(last[ci] - 100) < Math.abs(prev[ci] - 100) ? good : badC;else tc = '#8793a6';
        }
        return {
          name: D.rows[ci].name,
          val: fmtV(last[ci]),
          trend,
          tc,
          txt: on ? '#e4e9f0' : '#b8c1ce',
          bg: on ? '#121b2b' : 'transparent',
          box: on ? colorOf(ci) : '#3a475c',
          fill: on ? colorOf(ci) : 'transparent',
          check: on ? '✓' : '',
          onClick: () => setSel(on ? sel.filter(x => x !== ci) : [...sel, ci])
        };
      })
    };
    out.sMarks = foodMarks;
    if (isFood) out.sYAxes = foodAxis(out.sYTicks);
    out.sMixLeg = [];
    out.sLines = out.sLines.map(l => ({
      ...l,
      dash: 'none'
    }));
    if (st.sMix) return {
      ...out,
      ...this.evoMix(st, EM, buckets, week, sel, colorOf, xt)
    };
    out.sAxes = [['fecha', 'Fechas'], ['jaula', 'Jaulas']].map(([key, label]) => ({
      label,
      bg: (inv ? 'jaula' : 'fecha') === key ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: (inv ? 'jaula' : 'fecha') === key ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState({
        sAxis: key
      })
    }));
    if (!inv) return out;
    // Las jaulas quedan en X y cada período de la resolución elegida es una serie.
    const perVals = g;
    const live = perVals.flat().filter(Number.isFinite);
    const rm = k === 'sfr' ? Math.max(150, ...live) : Math.max(...live, 0.001) * 1.05;
    const st2 = (() => {
      const s = rm / 5,
        p = Math.pow(10, Math.floor(Math.log10(s))),
        n = s / p;
      return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
    })();
    const mx = st2 * Math.ceil(rm / st2),
      Y2 = v => (100 - v / mx * 100).toFixed(2),
      dayColor = di => 'oklch(0.72 0.11 ' + (235 + di * 47) % 360 + ')',
      daySlot = 0.76 / Math.max(1, nB);
    const hc = st.sHc ?? -1;
    const cageFoodMarks = [];
    const bars2 = buckets.map((_, di) => {
      let d = '';
      perVals.forEach((row, ci) => {
        const v = row[di];
        if (v == null || v <= 0) return;
        const x0 = ci + 0.12 + di * daySlot,
          x1 = x0 + daySlot * 0.88;
        if (isFood) {
          cageFoodMarks.push(foodMark(v, x0, x1, mx, nC, dayColor(di)));
          return;
        }
        d += 'M' + x0.toFixed(3) + ' 100 V' + Y2(v) + ' H' + x1.toFixed(3) + ' V100 Z ';
      });
      return {
        d: d || 'M0 0',
        c: dayColor(di)
      };
    });
    const idx2 = e => {
      const r = e.currentTarget.getBoundingClientRect();
      return Math.max(0, Math.min(nC - 1, Math.floor((e.clientX - r.left) / r.width * nC)));
    };
    const L = [];
    if (k === 'sfr') L.push({
      lab: '100 % obj.',
      c: '#e4e9f0',
      t: +Y2(100)
    });
    L.sort((a, b) => a.t - b.t);
    let pp = -99;
    L.forEach(e => {
      if (e.t - pp < 6) e.t = pp + 6;
      pp = e.t;
      e.top = e.t + '%';
    });
    const cageTipRows = hc < 0 ? [] : buckets.map((bucket, di) => ({
      name: this.periodLabel(bucket),
      c: dayColor(di),
      val: fmtV(perVals[hc][di]),
      value: perVals[hc][di]
    })).filter(row => Number.isFinite(row.value) && row.value !== 0);
    const dayLegend = buckets.map((bucket, di) => ({
      name: this.periodLabel(bucket),
      c: dayColor(di)
    }));
    const cageYTicks = [...Array(Math.round(mx / st2) + 1)].map((_, i) => ({
      pos: (i * st2 / mx * 100).toFixed(2) + '%',
      lab: em.tickFmt ? em.tickFmt(i * st2, st2) : st2 < 1 ? (i * st2).toFixed(st2 < 0.1 ? 2 : 1) : String(Math.round(i * st2))
    }));
    return {
      ...out,
      sTipFoot: em.label + (this.props.groupMode === 'day' ? ' · todos los días del período' : ' · todos los períodos seleccionados') + ' · clic para marcar/desmarcar la jaula',
      showTypes: false,
      sGroups: [],
      sVB: '0 0 ' + nC + ' 100',
      sBars: isFood ? [] : bars2,
      sMarks: cageFoodMarks,
      sLines: [],
      sAvg: 'M0 0',
      sAvgC: '#e4e9f0',
      sAvgW: 2,
      sRef: k === 'sfr' ? 'M0 ' + Y2(100) + ' H' + nC : 'M0 0',
      sEnd: L,
      sYTicks: cageYTicks,
      sYAxes: isFood ? foodAxis(cageYTicks) : out.sYAxes,
      sXTicks: D.rows.map((r, ci) => ({
        pos: ((ci + 0.5) / nC * 100).toFixed(2) + '%',
        lab: r.name.replace(' A', '')
      })),
      sLegend: dayLegend,
      sLegend8: dayLegend,
      sMove: e => {
        const i = idx2(e);
        if (i !== this.state.sHc) this.setState({
          sHc: i
        });
      },
      sClick: e => {
        const i = idx2(e);
        setSel(sel.includes(i) ? sel.filter(x => x !== i) : [...sel, i]);
      },
      sX: hc < 0 ? '0%' : ((hc + 0.5) / nC * 100).toFixed(2) + '%',
      sTipDisp: cageTipRows.length ? 'flex' : 'none',
      sTipTx: hc >= 0 && hc / nC > 0.6 ? "translateX(calc(-100% - 0.625vw))" : "translateX(0.625vw)",
      sTipDate: hc < 0 ? '' : D.rows[hc].name,
      sTipRows: cageTipRows
    };
  },
  roundBars(d) {
    const N = '(-?\\d*\\.?\\d+)',
      re = new RegExp('M\\s*' + N + '\\s+' + N + '\\s*V\\s*' + N + '\\s*([Hh])\\s*' + N + '\\s*V\\s*' + N + '\\s*Z', 'g');
    return d.replace(re, (m, a, b, c, H, e, g) => {
      const x0 = +a,
        yb = +b,
        yt = +c,
        x1 = H === 'h' ? x0 + +e : +e,
        w = x1 - x0,
        h = yb - yt;
      if (w <= 0 || h <= 0 || Math.abs(+g - yb) > 0.01) return m;
      const rx = w * 0.45,
        ry = Math.min(h / 2, Math.max(1.2, yb * 0.035)),
        f = v => +v.toFixed(2);
      return 'M' + f(x0) + ' ' + f(yb) + ' V' + f(yt + ry) + ' Q' + f(x0) + ' ' + f(yt) + ' ' + f(x0 + rx) + ' ' + f(yt) + ' H' + f(x1 - rx) + ' Q' + f(x1) + ' ' + f(yt) + ' ' + f(x1) + ' ' + f(yt + ry) + ' V' + f(yb) + ' Z';
    });
  },
  roundAll(o, depth = 0) {
    if (typeof o === 'string') return o.length > 8 && o[0] === 'M' && o.indexOf('Z') > 0 && /[Vv]/.test(o) ? this.roundBars(o) : o;
    if (!o || typeof o !== 'object' || depth > 8) return o;
    if (Array.isArray(o)) {
      let ch = false;
      const r = o.map(x => {
        const y = this.roundAll(x, depth + 1);
        if (y !== x) ch = true;
        return y;
      });
      return ch ? r : o;
    }
    if (Object.getPrototypeOf(o) !== Object.prototype || o.$$typeof) return o;
    let ch = false;
    const r = {};
    for (const key in o) {
      const y = this.roundAll(o[key], depth + 1);
      if (y !== o[key]) ch = true;
      r[key] = y;
    }
    return ch ? r : o;
  }
};
export default logic;
